import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  BATCH_PRESETS,
  multipleLabel,
  parseAmount,
  isValidScale,
  scaleForYield,
  scaledYield,
  type BatchScale,
} from "@/lib/recipes/staged-method";

interface BatchScaleControlProps {
  scale: BatchScale;
  onScaleChange: (scale: BatchScale) => void;
  yieldQuantity?: number | null;
  yieldUom?: string | null;
}

/**
 * How much to make: the presets (half, full, 5×, 10×), a typed multiple of the
 * recipe, and — when the recipe has a yield — the yield wanted, in the
 * recipe's own yield unit, which sets the multiple for you.
 */
export function BatchScaleControl({ scale, onScaleChange, yieldQuantity, yieldUom }: BatchScaleControlProps) {
  const { t } = useLingui();
  const [multipleText, setMultipleText] = useState("");
  const [yieldText, setYieldText] = useState("");
  const hasYield = yieldQuantity != null && Number(yieldQuantity) > 0;
  const yieldAmount = yieldText === "" ? null : parseAmount(yieldText);
  const yieldScale = yieldAmount === null ? null : scaleForYield(yieldAmount, yieldQuantity);
  const multiple = multipleText === "" ? null : parseAmount(multipleText);

  const pickPreset = (preset: BatchScale) => {
    setMultipleText("");
    setYieldText("");
    onScaleChange(preset);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label={t`Batch size`} className="flex items-center gap-1 rounded-xl bg-(--color-surface-muted) p-1">
        {BATCH_PRESETS.map((preset) => {
          const label = preset === 0.5 ? t`Half` : preset === 1 ? t`Full` : multipleLabel(preset);
          const count = scaledYield(yieldQuantity, preset);
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={scale === preset}
              onClick={() => pickPreset(preset)}
              className={cn(
                "rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors",
                scale === preset
                  ? "bg-(--color-surface-sidebar) text-(--color-text-on-dark)"
                  : "text-(--color-text-secondary) hover:text-(--color-text-primary)",
              )}
            >
              {count ? `${label} · ${count}` : label}
            </button>
          );
        })}
      </div>
      <label className="flex items-center gap-1.5 text-sm text-(--color-text-secondary)">
        <span>{t`Custom ×`}</span>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          value={multipleText}
          placeholder={String(Number(scale.toFixed(2)))}
          aria-invalid={multipleText !== "" && (multiple === null || !isValidScale(multiple))}
          onChange={(event) => {
            const text = event.target.value;
            setMultipleText(text);
            setYieldText("");
            const next = text === "" ? null : parseAmount(text);
            if (next !== null && isValidScale(next)) onScaleChange(next);
          }}
          className="h-9 w-20 border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary)"
        />
      </label>
      {hasYield && (
        <label className="flex items-center gap-1.5 text-sm text-(--color-text-secondary)">
          <span>{t`Desired yield`}</span>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={yieldText}
            aria-invalid={yieldText !== "" && yieldScale === null}
            onChange={(event) => {
              const text = event.target.value;
              setYieldText(text);
              setMultipleText("");
              const target = text === "" ? null : parseAmount(text);
              const next = target === null ? null : scaleForYield(target, yieldQuantity);
              if (next !== null) onScaleChange(next);
            }}
            className="h-9 w-24 border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary)"
          />
          {yieldUom && <span>{yieldUom}</span>}
        </label>
      )}
    </div>
  );
}
